/**
 * Cognitive Bias Detection types
 *
 * Represents the output of AI-assisted bias detection applied to prioritization
 * decisions, session results, and item portfolios. Advisory only — never alters
 * rankings, scores, or any stored state.
 */

// ── Taxonomy ─────────────────────────────────────────────────────────────────

export type BiasSeverity = 'high' | 'medium' | 'low';

export type BiasCategory =
  | 'group_dynamics'       // Groupthink, HiPPO, bandwagon
  | 'cognitive_shortcuts'  // Availability, recency, anchoring
  | 'strategic_framing'    // Sunk cost, status quo, narrative fallacy
  | 'confidence'           // Optimism bias, planning fallacy, overconfidence;

export type BiasType =
  // Group dynamics
  | 'hippo_effect'          // Highest Paid Person's Opinion dominates
  | 'groupthink'            // Dissent is suppressed for social harmony
  | 'bandwagon_effect'      // Herd mentality in scoring
  // Cognitive shortcuts
  | 'recency_bias'          // Over-weighting of recent events
  | 'availability_heuristic'// Ease of recall drives perceived importance
  | 'anchoring_bias'        // First data point anchors all subsequent judgments
  | 'confirmation_bias'     // Seeking only confirming signals
  // Strategic framing
  | 'sunk_cost_fallacy'     // Past investment drives future commitment
  | 'status_quo_bias'       // Preference for the current state of affairs
  | 'narrative_fallacy'     // Compelling story substitutes for evidence
  // Confidence
  | 'optimism_bias'         // Overestimate positive outcomes
  | 'planning_fallacy'      // Underestimate time, cost, and complexity;

export const BIAS_META: Record<BiasType, { label: string; category: BiasCategory; kahneman: string }> = {
  hippo_effect: {
    label: 'HiPPO Effect',
    category: 'group_dynamics',
    kahneman: 'System 1 deference to authority overrides independent System 2 evaluation.',
  },
  groupthink: {
    label: 'Groupthink',
    category: 'group_dynamics',
    kahneman: 'Social cohesion pressure short-circuits deliberate, slow thinking.',
  },
  bandwagon_effect: {
    label: 'Bandwagon Effect',
    category: 'group_dynamics',
    kahneman: 'Social proof acts as a cognitive shortcut, displacing independent judgment.',
  },
  recency_bias: {
    label: 'Recency Bias',
    category: 'cognitive_shortcuts',
    kahneman: 'Recent events are more available in memory, inflating their perceived importance.',
  },
  availability_heuristic: {
    label: 'Availability Heuristic',
    category: 'cognitive_shortcuts',
    kahneman: 'We judge frequency and importance by how easily examples come to mind.',
  },
  anchoring_bias: {
    label: 'Anchoring Bias',
    category: 'cognitive_shortcuts',
    kahneman: 'The first number or framing encountered anchors all subsequent estimates.',
  },
  confirmation_bias: {
    label: 'Confirmation Bias',
    category: 'cognitive_shortcuts',
    kahneman: 'We seek and weight evidence that confirms existing beliefs and ignore contradictions.',
  },
  sunk_cost_fallacy: {
    label: 'Sunk Cost Fallacy',
    category: 'strategic_framing',
    kahneman: 'Past investment creates loss aversion, making rational abandonment feel like defeat.',
  },
  status_quo_bias: {
    label: 'Status Quo Bias',
    category: 'strategic_framing',
    kahneman: 'Changing from the default is coded as a loss; staying put feels safe even when suboptimal.',
  },
  narrative_fallacy: {
    label: 'Narrative Fallacy',
    category: 'strategic_framing',
    kahneman: 'Coherent stories feel more true than they are, overriding statistical evidence.',
  },
  optimism_bias: {
    label: 'Optimism Bias',
    category: 'confidence',
    kahneman: 'We systematically overestimate the probability and magnitude of positive outcomes.',
  },
  planning_fallacy: {
    label: 'Planning Fallacy',
    category: 'confidence',
    kahneman: 'We use best-case scenarios when planning, ignoring the outside view of similar efforts.',
  },
};

export const BIAS_CATEGORY_META: Record<BiasCategory, { label: string; color: string }> = {
  group_dynamics:     { label: 'Group Dynamics',    color: 'violet' },
  cognitive_shortcuts:{ label: 'Cognitive Shortcuts', color: 'amber' },
  strategic_framing:  { label: 'Strategic Framing', color: 'rose' },
  confidence:         { label: 'Confidence',        color: 'sky' },
};

// ── Input context ─────────────────────────────────────────────────────────────

export interface BiasItemContext {
  id: string;
  title: string;
  category?: string;
  bucket?: string;
  rank?: number;
  score?: number;
  scoreStdDev?: number;
  votes?: number;
  cyclesAppeared?: number;
  resourcesConsumed?: string;
  createdAt?: string;
  lastUpdatedAt?: string;
  steward?: string;
  relationships?: Array<{
    type: string;
    otherTitle: string;
    otherRank?: number;
  }>;
}

export interface BiasParticipantContext {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  seniorityLevel?: 'executive' | 'director' | 'manager' | 'individual_contributor';
  votedItems?: string[];
  avgScore?: number;
}

export interface BiasAnalysisContext {
  // What we're analyzing
  analysisScope: 'session' | 'cycle' | 'portfolio';

  // Session context (when scope = session)
  sessionName?: string;
  sessionTool?: string;
  sessionCompletedAt?: string;

  // Cycle context
  cycleName?: string;
  cycleNumber?: number;
  previousCycleTopItemTitles?: string[];

  // Items
  items: BiasItemContext[];
  cutLinePosition?: number;

  // Participants & voting
  participants: BiasParticipantContext[];
  scoreVarianceAcrossItems?: number;
  participantAgreementRate?: number;
  dominantVoterEmail?: string;

  // Criteria
  criteriaLabels?: string[];

  // Workspace signals
  workspaceName?: string;
  recentIncidentOrEvent?: string;
}

// ── Output ────────────────────────────────────────────────────────────────────

export interface DetectedBias {
  biasType: BiasType;
  severity: BiasSeverity;
  confidence: number; // 0.0–1.0

  /** One sentence: what's happening in this specific session/cycle. */
  headline: string;

  /** 2–3 sentences: what's happening and why it matters for this team. */
  explanation: string;

  /**
   * Concrete illustration using actual item names, participant details,
   * or voting patterns from the provided context. Makes it undeniable.
   */
  illustration: string;

  /** Actionable countermeasure tailored to what was observed. */
  mitigation: string;

  /** Item titles or participant names used as evidence. */
  evidenceItemTitles?: string[];
}

export interface BiasDetectionResult {
  analysisScope: 'session' | 'cycle' | 'portfolio';
  contextLabel: string;
  generatedAt: string;
  detectedBiases: DetectedBias[];
  overallRiskLevel: BiasSeverity | 'none';
  summary: string;
}
